import { operands as conditionOperatorGroups, operatorAliases } from '@/app/component/conditions/definitions'
import { validateConditionValue } from '@/app/component/conditions/validation'
import type { Condition, ConditionFunc } from '@/app/types'

const CONDITION_OPERATORS = new Set(conditionOperatorGroups.flatMap(({ data }) => data.map(({ value }) => value)))

export type EditableQuotaConditions = {
  groups: Condition[]
  supported: boolean
}

const normalizeConditionOperator = (operator: unknown): ConditionFunc | undefined => {
  if (typeof operator !== 'string') {
    return undefined
  }

  const conditionOperator = operator as ConditionFunc
  const normalizedOperator = operatorAliases[conditionOperator] ?? conditionOperator

  return CONDITION_OPERATORS.has(normalizedOperator) ? normalizedOperator : undefined
}

const normalizeEditableCondition = (condition: unknown): Condition | undefined => {
  if (!Array.isArray(condition)) {
    return undefined
  }

  const operator = normalizeConditionOperator(condition[0])
  if (!operator || operator === 'and' || operator === 'or') {
    return undefined
  }

  return [operator, ...condition.slice(1)] as Condition
}

const normalizeEditableConditionGroup = (group: unknown): Condition | undefined => {
  if (!Array.isArray(group)) {
    return undefined
  }

  if (group[0] !== 'and') {
    const condition = normalizeEditableCondition(group)

    return condition ? (['and', condition] as Condition) : undefined
  }

  const conditions = group.slice(1).map(normalizeEditableCondition)

  return conditions.length === group.length - 1 && conditions.every(Boolean)
    ? (['and', ...(conditions as Condition[])] as Condition)
    : undefined
}

export const toEditableQuotaConditions = (conditions: unknown): EditableQuotaConditions => {
  if (conditions === null) {
    return { groups: [], supported: true }
  }
  if (!Array.isArray(conditions)) {
    return { groups: [], supported: false }
  }
  if (!conditions.length) {
    return { groups: [], supported: true }
  }

  if (conditions[0] === 'or') {
    const groups = conditions.slice(1).map(normalizeEditableConditionGroup)

    return groups.length === conditions.length - 1 && groups.every(Boolean)
      ? { groups: groups as Condition[], supported: true }
      : { groups: [], supported: false }
  }

  const group = normalizeEditableConditionGroup(conditions)
  if (group) {
    return { groups: [group], supported: true }
  }

  const legacyConditions = conditions.map(normalizeEditableCondition)

  return legacyConditions.every(Boolean)
    ? { groups: [['and', ...(legacyConditions as Condition[])] as Condition], supported: true }
    : { groups: [], supported: false }
}

export const fromEditableQuotaConditions = (groups: Condition[]): Condition | null =>
  groups.length ? ['or', ...groups] : null

const isEditableConditionValid = (condition: Condition): boolean => {
  const [operator, field] = condition

  return Boolean(
    operator &&
      operator !== 'and' &&
      operator !== 'or' &&
      typeof field === 'string' &&
      field &&
      !validateConditionValue(condition),
  )
}

export const areEditableQuotaConditionsValid = (groups: Condition[]): boolean =>
  groups.every(
    (group) =>
      group[0] === 'and' &&
      group.length > 1 &&
      group.slice(1).every((condition) => Array.isArray(condition) && isEditableConditionValid(condition as Condition)),
  )
